import { Clear, Tune } from "@mui/icons-material";
import { Button, ClickAwayListener, Divider, Fade, Paper, Popper, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { ORDER_SOURCE_TYPE } from "src/constant";
import useWareHouseList from "src/hooks/warehouse/useWareHouseList";
import BtnGroupDynamic from "src/components/shared/BtnGroupDynamic";
import { STATUS } from "src/pages/debt/const/constants";

const paymentMethodFilter = (handlePickOptionSearch, paramsQuery) => ({
  id: "paymentMethod",
  name: "paymentMethod",
  tatCaKey: "paymentMethod",
  title: "Hình thức thanh toán",
  comp: (
    <BtnGroupDynamic
      handlePickOptionSearch={handlePickOptionSearch}
      paramsQuery={paramsQuery}
      path="/api/v1/payment-methods"
      name="paymentMethod"
      params={{ limit: 100, skip: 0 }}
    />
  ),
});

const statusFilter = {
  id: "status",
  name: "status",
  tatCaKey: "status",
  title: "Trạng thái",
  data: [
    { _id: STATUS.PAID, name: "Đã thanh toán" },
    { _id: STATUS.UNPAID, name: "Chưa thanh toán" },
  ],
};

const warehouseOutFilter = (warehouseData) => ({
  id: "warehouseOut",
  name: "warehouse",
  tatCaKey: "warehouse",
  title: "Kho Xuất",
  data: Array.isArray(warehouseData) ? warehouseData : [],
});

const warehouseDebtFilter = (warehouseData) => ({
  id: "warehouseDebt",
  name: "warehouseDebt",
  tatCaKey: "warehouseDebt",
  title: "Kho Nhập",
  data: Array.isArray(warehouseData) ? warehouseData : [],
});

const FILTER_KEYS = {
  all: ["paymentMethod"],
  unpaid: ["paymentMethod", "warehouse", "warehouseDebt"],
  overdue: ["paymentMethod", "status", "warehouse", "warehouseDebt"],
  history: ["paymentMethod", "warehouse", "warehouseDebt"],
};

const FILTER_MAP = {
  all: (handlePickOptionSearch, paramsQuery) => [paymentMethodFilter(handlePickOptionSearch, paramsQuery)],
  unpaid: (handlePickOptionSearch, paramsQuery, warehouseData) => [
    paymentMethodFilter(handlePickOptionSearch, paramsQuery),
    warehouseOutFilter(warehouseData),
    warehouseDebtFilter(warehouseData),
  ],
  overdue: (handlePickOptionSearch, paramsQuery, warehouseData) => [
    paymentMethodFilter(handlePickOptionSearch, paramsQuery),
    statusFilter,
    warehouseOutFilter(warehouseData),
    warehouseDebtFilter(warehouseData),
  ],
  history: (handlePickOptionSearch, paramsQuery, warehouseData) => [
    paymentMethodFilter(handlePickOptionSearch, paramsQuery),
    warehouseOutFilter(warehouseData),
    warehouseDebtFilter(warehouseData),
  ],
};

/**
 * Bộ lọc dành riêng cho quản lý công nợ sales.
 * @param {"all"|"unpaid"|"overdue"|"history"} variant
 */
export default function DebtSalesFilter({ setParamsQuery, paramsQuery, variant = "all" }) {
  const needsWarehouseList = ["unpaid", "overdue", "history"].includes(variant);
  const { data: warehouseList } = useWareHouseList(needsWarehouseList ? { limit: 100, skip: 0 } : null);
  const warehouseData = warehouseList?.data;

  const [popperProps, setPopperProps] = useState({
    open: false,
    anchorEl: null,
    placement: "bottom-end",
  });

  const handlePickOptionSearch = ({ name, value }) => {
    setParamsQuery({
      ...paramsQuery,
      [name]: value === ORDER_SOURCE_TYPE.All ? undefined : value,
    });
  };

  const isTatCaActiveForFilterGroup = (groupName) => {
    return [ORDER_SOURCE_TYPE.All, undefined]?.includes(paramsQuery?.[groupName]);
  };

  const filterList = FILTER_MAP[variant]?.(handlePickOptionSearch, paramsQuery, warehouseData) ?? [];

  const handleSetPop = (placement) => (event) => {
    setPopperProps({
      open: !popperProps.open,
      anchorEl: event.currentTarget,
      placement,
    });
  };

  const handleClosePopper = () => {
    setPopperProps((prev) => ({ ...prev, open: false }));
  };

  const hasActiveFilters = useMemo(() => {
    const filterKeys = FILTER_KEYS[variant] ?? [];
    return filterKeys.some((k) => {
      const val = paramsQuery?.[k];
      return val !== undefined && val !== null && val !== "";
    });
  }, [paramsQuery, variant]);

  const handleClearFilters = () => {
    setParamsQuery((prev) => {
      const next = { ...(prev ?? {}) };
      (FILTER_KEYS[variant] ?? []).forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  return (
    <ClickAwayListener onClickAway={handleClosePopper}>
      <Stack sx={{ display: "inline-block" }}>
        <Button
          size="small"
          variant="outlined"
          sx={{ height: "100%", maxHeight: "36.5px" }}
          startIcon={<Tune />}
          onClick={handleSetPop("bottom-end")}
        >
          Lọc
        </Button>
        <Popper
          disablePortal
          sx={{
            zIndex: 1200,
            maxWidth: "900px",
            width: "90%",
            maxHeight: "330px",
            height: "auto",
            overflow: "auto",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          }}
          open={popperProps.open}
          anchorEl={popperProps.anchorEl}
          placement={popperProps.placement}
          transition
        >
          {({ TransitionProps }) => (
            <Fade
              {...TransitionProps}
              timeout={350}
            >
              <Paper sx={{ p: "12px", display: "flex", flexDirection: "column" }}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ pb: 1, mb: 1, borderBottom: "1px solid var(--light-grey)" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<Clear />}
                    disabled={!hasActiveFilters}
                    onClick={handleClearFilters}
                    sx={{ fontSize: "12px" }}
                  >
                    Xóa bộ lọc
                  </Button>
                </Stack>
                {filterList.map((v) => (
                  <Stack
                    direction="column"
                    key={v.id}
                    spacing={1}
                    mb={1.5}
                    pd={1.5}
                  >
                    <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{v.title}</Typography>
                    <Stack
                      direction="row"
                      gap={1}
                    >
                      <Button
                        onClick={() =>
                          handlePickOptionSearch({
                            name: v.tatCaKey,
                            value: ORDER_SOURCE_TYPE.All,
                          })
                        }
                        sx={{
                          border: isTatCaActiveForFilterGroup(v.tatCaKey) ? "1px solid var(--confirm-txt)" : "1px solid var(--light-grey)",
                          color: isTatCaActiveForFilterGroup(v.tatCaKey) ? "var(--confirm-txt)" : "var(--black)",
                          display: "inline",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                      >
                        Tất cả
                      </Button>
                      {!v?.comp &&
                        v?.data?.map((val) => (
                          <Button
                            key={val?._id}
                            onClick={() =>
                              handlePickOptionSearch({
                                name: v?.name,
                                value: val?._id,
                              })
                            }
                            sx={{
                              border: [val?._id]?.includes(paramsQuery?.[v?.name]) ? "1px solid var(--confirm-txt)" : "1px solid var(--light-grey)",
                              color: [val?._id]?.includes(paramsQuery?.[v?.name]) ? "var(--confirm-txt)" : "var(--black)",
                              display: "inline",
                              p: "6px 12px !important",
                              borderRadius: "4px",
                              fontSize: "12px",
                            }}
                          >
                            {val?.name}
                          </Button>
                        ))}
                      {v?.comp ?? ""}
                    </Stack>
                    <Divider sx={{ color: "var(--light-grey)" }} />
                  </Stack>
                ))}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Stack>
    </ClickAwayListener>
  );
}
